import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import style from './InfoTooltip.module.css';

const VIEWPORT_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 280;
const GAP_BELOW = 8;

export const OPEN_MARKET_TRADES_TOOLTIP =
  "Open-market trades are shares insiders bought or sold at market price with their own money. They're the most meaningful signal. Checking this filter hides awards, option exercises, gifts and tax withholding, which are mostly compensation.";

function InfoIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.25" />
      <circle cx="8" cy="5.1" r="0.85" fill="currentColor" />
      <path
        d="M8 7.15v4.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

function clampPlacement(triggerRect) {
  const width = Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  let left = triggerRect.left + triggerRect.width / 2 - width / 2;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN));
  const top = triggerRect.bottom + GAP_BELOW;
  return { top, left, width };
}

const InfoTooltip = ({
  content,
  text,
  label = 'More information',
  children,
}) => {
  const tooltipContent = content ?? text;
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [placement, setPlacement] = useState(null);
  const open = hovering || pinned;

  useEffect(() => {
    setMounted(true);
  }, []);

  const updatePlacement = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setPlacement(clampPlacement(trigger.getBoundingClientRect()));
  }, []);

  const show = useCallback(() => {
    updatePlacement();
    setHovering(true);
  }, [updatePlacement]);

  const hide = useCallback(() => {
    setHovering(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePlacement();
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);

    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [open, updatePlacement]);

  const tooltip = mounted && open && placement
    ? createPortal(
      <span
        id={tooltipId}
        role="tooltip"
        className={style.popover}
        style={{
          top: `${placement.top}px`,
          left: `${placement.left}px`,
          width: `${placement.width}px`,
        }}
      >
        {tooltipContent}
      </span>,
      document.body,
    )
    : null;

  return (
    <span
      className={`${style.group} ${open ? style.groupOpen : ''}`}
      onMouseEnter={show}
      onMouseLeave={() => {
        if (!pinned) hide();
      }}
      onFocus={show}
      onBlur={(event) => {
        if (!pinned && !event.currentTarget.contains(event.relatedTarget)) {
          hide();
        }
      }}
    >
      {children}
      <button
        ref={triggerRef}
        type="button"
        className={style.trigger}
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={() => {
          if (!pinned) hide();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          updatePlacement();
          setPinned((current) => !current);
        }}
      >
        <InfoIcon />
      </button>
      {tooltip}
    </span>
  );
};

InfoTooltip.propTypes = {
  content: PropTypes.node,
  text: PropTypes.string,
  label: PropTypes.string,
  children: PropTypes.node,
};

export default InfoTooltip;
